import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Check, Crown, Zap, CreditCard, AlertCircle, RefreshCw, XCircle, Loader2, Calendar, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

const Subscription = () => {
  const { t, currentLanguage } = useLanguage();

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);


  const getPlanIcon = (planId) => {
    const icons = {
      free: Zap,
      pro_monthly: Crown,
      pro_yearly: Crown,
      basic: Zap
    };
    return icons[planId] || Zap;
  };

  useEffect(() => {
    fetchSubscriptionData();
  }, []);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      setAuthError(false);

      // Try to fetch current subscription
      let subscriptionData = null;
      try {
        const response = await api.get('/subscription.php');
        console.log('Raw subscription response:', response);

        if (response && typeof response === 'object') {
          if (response.success === false) {
            console.warn('Subscription API returned error:', response.error);

            if (response.error === 'Restaurant not found' ||
              response.error.includes('Restaurant') ||
              response.status === 404) {
              setAuthError(true);
              toast.error(t('subscription.restaurantNotFound', 'Please log in or contact support'));
            }
          } else if (response.data) {
            subscriptionData = response.data;
          } else if (response.success === true) {
            subscriptionData = response.data || response;
          } else {
            subscriptionData = response;
          }
        }
      } catch (apiError) {
        console.error('Subscription API call failed:', apiError);
        if (apiError.response) {
          const status = apiError.response.status;
          const data = apiError.response.data;
          if (status === 404 || (data && data.error && data.error.includes('Restaurant'))) {
            setAuthError(true);
            toast.error(t('subscription.authError', 'Authentication required. Please log in.'));
          } else if (status === 401 || status === 403) {
            setAuthError(true);
            toast.error(t('subscription.sessionExpired', 'Session expired. Please log in again.'));
          }
        }
      }

      // Set current subscription with safe fallback
      if (!subscriptionData && !authError) {
        setCurrentSubscription({
          is_active: false,
          plan: 'free',
          subscription: null,
          plan_limits: {
            plan: 'free',
            features: [
              t('plans.free.features.0', 'Up to 3 staff accounts'),
              t('plans.free.features.1', 'Basic QR menu'),
              t('plans.free.features.2', '50 orders/month'),
              t('plans.free.features.3', 'Email support')
            ],
            max_staff: 3,
            max_orders: 50,
            max_tables: 5
          }
        });
      } else if (subscriptionData) {
        setCurrentSubscription(subscriptionData);
      }

      // Fetch available plans (only if not auth error)
      if (!authError) {
        let plansData = null;
        try {
          const plansResponse = await api.post('/subscription.php', {
            action: 'get_plans'
          });

          console.log('Plans response:', plansResponse);

          if (plansResponse && plansResponse.success) {
            plansData = plansResponse.data;
          } else if (plansResponse && Array.isArray(plansResponse)) {
            plansData = plansResponse; // Direct array response
          }
        } catch (plansError) {
          console.warn('Plans API call failed:', plansError);
        }

        if (plansData && Array.isArray(plansData)) {
          const freePlan = getStaticPlans().find(p => p.id === 'free');
          const apiPlans = plansData.filter(plan =>
            plan.id && plan.id !== 'free' && plan.id !== 'basic_monthly'
          );
          setPlans([freePlan, ...apiPlans]);
        } else {
          console.log('Using static plans as fallback');
          setPlans(getStaticPlans());
        }
      } else {
        setPlans(getStaticPlans());
      }

    } catch (error) {
      console.error('Failed to fetch subscription data:', error);
      if (!authError) {
        setCurrentSubscription({
          is_active: false,
          plan: 'free',
          subscription: null,
          plan_limits: {
            plan: 'free',
            features: [],
            max_staff: 3,
            max_orders: 50,
            max_tables: 5
          }
        });
        setPlans(getStaticPlans());
      }
    } finally {
      setLoading(false);
    }
  };

  const getStaticPlans = () => {
    return [
      {
        id: 'free',
        name: t('plans.free.name', 'Free'),
        price: 0,
        interval: 'month',
        stripe_price_id: null,
        features: [
          t('plans.free.features.0', 'Up to 3 staff accounts'),
          t('plans.free.features.1', 'Basic QR menu'),
          t('plans.free.features.2', '50 orders/month'),
          t('plans.free.features.3', 'Email support')
        ],
        limits: {
          max_staff: 3,
          max_orders: 50,
          max_tables: 5
        } 
      },
      {
        id: 'pro_monthly',
        name: t('plans.pro.name', 'Professional'),
        price: 99,
        interval: 'month',
        stripe_price_id: 'price_1TOOq57qJJQapbT0ZozM6b2Y',
        features: [
          t('plans.pro.features.0', 'Up to 15 staff members'),
          t('plans.pro.features.1', 'Up to 50 tables'),
          t('plans.pro.features.2', 'Advanced menu management'),
          t('plans.pro.features.3', 'Real-time order tracking'),
          t('plans.pro.features.4', 'Analytics dashboard')
        ],
        limits: {
          max_staff: -1,
          max_tables: -1,
          max_orders: -1
        }
      },
      {
        id: 'pro_yearly',
        name: t('plans.pro.name', 'Professional'),
        price: 1089,
        interval: 'year',
        stripe_price_id: 'price_1TOOqn7qJJQapbT0jnMQjVba',
        features: [
          t('plans.pro.features.0', 'Up to 15 staff members'),
          t('plans.pro.features.1', 'Up to 50 tables'),
          t('plans.pro.features.2', 'Advanced menu management'),
          t('plans.pro.features.3', 'Real-time order tracking'),
          t('plans.pro.features.4', 'Analytics dashboard'),
          t('plans.pro.features.5', 'Save 16% with annual billing')
        ],
        limits: {
          max_staff: -1,
          max_tables: -1,
          max_orders: -1
        }
      }
    ];
  };

  const getCurrentPlan = () => {
    if (!currentSubscription) return 'free';

    // If cancelled at period end, always show as free
    if (currentSubscription.is_cancelled_at_period_end) {
      return 'free';
    }

    // If restaurant plan is free, show as free
    if (currentSubscription.restaurant_plan === 'free') {
      return 'free';
    }

    const plan = currentSubscription.plan ||
      currentSubscription.subscription?.plan ||
      currentSubscription.plan_limits?.plan ||
      'free';
    return plan;
  };

  const canUpgrade = () => {
    if (authError) return false;

    const currentPlan = getCurrentPlan();

    // Can't upgrade if already on pro_monthly or pro_yearly and not cancelled
    if ((currentPlan === 'pro_monthly' || currentPlan === 'pro_yearly') &&
      !currentSubscription?.is_cancelled_at_period_end &&
      currentSubscription?.subscription?.status === 'active') {
      return false;
    }

    // Can upgrade if on free or cancelled pro plan
    return (currentPlan === 'free' ||
      currentSubscription?.subscription?.status !== 'active' ||
      currentSubscription?.is_cancelled_at_period_end);
  };



  const handleUpgrade = async (plan) => {
    if (authError) {
      toast.error(t('subscription.authRequired', 'Please log in to upgrade'));
      return;
    }

    if (!plan.stripe_price_id) {
      toast.error(t('subscription.planNotAvailable', 'This plan is not available for upgrade'));
      return;
    }

    try {
      setIsProcessing(true);

      // Check if this is a trial attempt
      const isTrial = plan.id !== 'free' &&
        (!currentSubscription?.is_trial_used) &&
        currentPlan === 'free';

      console.log('Sending checkout request for plan:', plan.id, 'isTrial:', isTrial);

      // Use subscription.php with create_checkout action
      const response = await api.post('/subscription.php', {
        action: 'create_checkout',
        plan: plan.id,
        is_trial: isTrial,
        interval: plan.interval
      });

      console.log('Checkout response:', response);

      // Check if response exists
      if (!response) {
        throw new Error('No response from server');
      }

      // Handle the response - different possible formats
      let checkoutUrl = null;

      // Format 1: { success: true, data: { url: '...' } }
      if (response.success === true) {
        if (response.data && response.data.url) {
          checkoutUrl = response.data.url;
        }
        // Format 2: { success: true, url: '...' }
        else if (response.url) {
          checkoutUrl = response.url;
        }
      }
      // Format 3: Direct response with url (no success wrapper)
      else if (response.url) {
        checkoutUrl = response.url;
      }

      // If we found a URL, redirect
      if (checkoutUrl) {
        console.log('Redirecting to:', checkoutUrl);

        // If it's a trial, show a message about credit card
        if (isTrial) {
          toast.success(t('subscription.trialCreditCard',
            'You will be redirected to Stripe to enter your credit card for the 30-day free trial. You won\'t be charged until the trial ends.'));
        }

        // Small delay to ensure toast shows
        setTimeout(() => {
          window.location.href = checkoutUrl;
        }, 1000);
      } else {
        console.error('No URL found in response:', response);

        // Check if there's an error message
        if (response.message) {
          throw new Error(response.message);
        } else if (response.error) {
          throw new Error(response.error);
        } else {
          throw new Error('No checkout URL received');
        }
      }

    } catch (error) {
      console.error('Upgrade error:', error);
      toast.error(t('subscription.upgradeError', 'Upgrade failed: ') + error.message);
      setIsProcessing(false);
    }
  };

  const handleSyncSubscription = async () => {
    if (authError) {
      toast.error(t('subscription.authRequired', 'Please log in to sync'));
      return;
    }

    setSyncing(true);
    try {
      const response = await api.post('/subscription.php', { action: 'sync' });

      if (response.success === false) {
        throw new Error(response.error || 'Sync failed');
      }

      // Show sync result message
      if (response.data && response.data.message) {
        toast.success(response.data.message);
      }

      // Refresh after a short delay
      setTimeout(() => {
        fetchSubscriptionData();
      }, 1000);

    } catch (error) {
      console.error('Sync error:', error);
      toast.error(t('subscription.syncError', 'Failed to sync subscription: ') + error.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (authError) {
      toast.error(t('subscription.authRequired', 'Please log in to cancel subscription'));
      return;
    }

    setCancelling(true);
    try {
      console.log('Attempting to cancel subscription...');

      const response = await api.post('/subscription.php', {
        action: 'cancel'
      });

      console.log('Cancel response:', response);

      if (response.success === false) {
        console.error('Cancel failed with error:', response.error);
        throw new Error(response.error || 'Cancel failed');
      }

      // Success - update local state
      console.log('Cancellation successful, refreshing data...');
      await fetchSubscriptionData();
      toast.success(t('subscription.cancelSuccess', 'Subscription cancelled successfully'));
      setShowCancelConfirm(false);

      // Show information about cancellation
      toast.success(t('subscription.cancellationEffective', 'Your subscription will remain active until the end of the current billing period'));

    } catch (error) {
      console.error('Cancel error details:', error);

      // Check if it's a specific error
      if (error.message && error.message.includes('Stripe')) {
        toast.error(t('subscription.stripeCancelError', 'Stripe cancellation failed. Please contact support.'));
      } else {
        toast.error(t('subscription.cancelError', 'Failed to cancel subscription: ') + error.message);
      }
    } finally {
      setCancelling(false);
    }
  };

  const showCancelConfirmation = () => {
    setShowCancelConfirm(true);
  };

  const hideCancelConfirmation = () => {
    setShowCancelConfirm(false);
  };

  const handleRetry = () => {
    fetchSubscriptionData();
  };

  const currentPlan = getCurrentPlan();
  const isActive = currentSubscription?.is_active &&
    !currentSubscription?.is_cancelled_at_period_end &&
    currentSubscription?.restaurant_plan !== 'free';
  const isTrialActive = currentSubscription?.is_trial_active ||
    currentSubscription?.subscription?.status === 'trialing' ||
    false;

  // Check if subscription is cancellable
  const isCancellable = (() => {
    // If no subscription at all, can't cancel
    if (!currentSubscription?.subscription) return false;

    // Must be on a paid plan (pro_monthly or pro_yearly)
    const isPaidPlan = currentPlan === 'pro_monthly' || currentPlan === 'pro_yearly';
    if (!isPaidPlan) return false;

    // Must not already be cancelling
    if (currentSubscription.subscription.cancel_at_period_end) return false;
    if (currentSubscription.is_cancelled_at_period_end) return false;

    // ✅ ALLOW CANCELLATION DURING TRIAL OR ACTIVE SUBSCRIPTION
    const isTrialing = currentSubscription.subscription.status === 'trialing';
    const isActive = currentSubscription.subscription.status === 'active';

    // Return true if either trialing OR active
    return isTrialing || isActive;
  })();

  // Add this debug log to see what's happening
  console.log('🔍 Cancel button check:', {
    hasSubscription: !!currentSubscription?.subscription,
    status: currentSubscription?.subscription?.status,
    isTrialing: currentSubscription?.subscription?.status === 'trialing',
    isActive: currentSubscription?.subscription?.status === 'active',
    isPaidPlan: currentPlan === 'pro_monthly' || currentPlan === 'pro_yearly',
    alreadyCancelling: currentSubscription?.subscription?.cancel_at_period_end,
    showButton: (currentSubscription?.subscription?.status === 'trialing' ||
      currentSubscription?.subscription?.status === 'active') &&
      (currentPlan === 'pro_monthly' || currentPlan === 'pro_yearly') &&
      !currentSubscription?.subscription?.cancel_at_period_end
  });

  const planIcons = {
    free: Zap,
    pro_monthly: Crown,
    pro_yearly: Crown,
    basic: Zap
  };

  const planGradients = {
    free: 'from-gray-500 to-gray-600',
    pro_monthly: 'from-blue-500 to-indigo-600',
    pro_yearly: 'from-blue-500 to-indigo-600',
    basic: 'from-gray-500 to-gray-600'
  };

  const planColors = {
    free: {
      bg: 'bg-gray-50',
      border: 'border-gray-300',
      ring: 'ring-gray-200',
      text: 'text-gray-700',
      badge: 'bg-gray-600'
    },
    pro_monthly: {
      bg: 'bg-blue-50',
      border: 'border-blue-500',
      ring: 'ring-blue-200',
      text: 'text-blue-700',
      badge: 'bg-blue-600'
    },
    pro_yearly: {
      bg: 'bg-blue-50',
      border: 'border-blue-500',
      ring: 'ring-blue-200',
      text: 'text-blue-700',
      badge: 'bg-blue-600'
    },
    basic: {
      bg: 'bg-gray-50',
      border: 'border-gray-300',
      ring: 'ring-gray-200',
      text: 'text-gray-700',
      badge: 'bg-gray-600'
    }
  };

  const currentPlanStyle = planColors[currentPlan] || planColors.free;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-12 w-12 md:h-16 md:w-16 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="min-h-screen bg-gray-50 py-6 md:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
              {t('subscription.title', 'Subscription Management')}
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-gray-600">
              {t('subscription.subtitle', 'Manage your subscription and billing preferences')}
            </p>
          </div>

          <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-6 md:p-8 text-center">
            <AlertCircle className="h-12 w-12 md:h-16 md:w-16 text-red-500 mx-auto mb-3 md:mb-4" />
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
              {t('subscription.authErrorTitle', 'Authentication Required')}
            </h2>
            <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">
              {t('subscription.authErrorMessage', 'You need to be logged in to view your subscription. Please log in and try again.')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
              <button
                onClick={handleRetry}
                className="bg-blue-600 text-white px-4 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl text-sm md:text-base font-semibold hover:bg-blue-700 transition-colors"
              >
                {t('subscription.retry', 'Retry')}
              </button>
              <button
                onClick={() => window.location.href = '/login'}
                className="bg-gray-600 text-white px-4 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl text-sm md:text-base font-semibold hover:bg-gray-700 transition-colors"
              >
                {t('subscription.goToLogin', 'Go to Login')}
              </button>
            </div>
          </div>

          <div className="mt-6 md:mt-8">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">
              {t('subscription.availablePlans', 'Available Plans')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
              {getStaticPlans().map((plan) => {
                const Icon = getPlanIcon(plan.id);

                return (
                  <div
                    key={plan.id}
                    className="bg-white rounded-xl md:rounded-2xl shadow-lg border-2 border-gray-200"
                  >
                    <div className="p-4 md:p-6 lg:p-8">
                      <div className="text-center mb-4 md:mb-6">
                        <div className={`inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl bg-gradient-to-r ${planGradients[plan.id]} text-white mb-3 md:mb-4`}>
                          <Icon className="h-5 w-5 md:h-8 md:w-8" />
                        </div>
                        <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-1 md:mb-2">
                          {plan.id === 'pro_monthly'
                            ? t('plans.pro.name', 'Professional')
                            : plan.id === 'pro_yearly'
                              ? t('plans.pro.name', 'Professional')
                              : t(`plans.${plan.id}.name`, plan.name)}
                        </h3>
                        <div className="flex items-baseline justify-center gap-1 md:gap-2">
                          <span className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">
                            CHF {plan.price}
                          </span>
                          <span className="text-sm md:text-base text-gray-600">
                            /{t(`subscription.intervals.${plan.interval}`, plan.interval)}
                          </span>
                        </div>
                      </div>

                      <ul className="space-y-2 md:space-y-3 mb-4 md:mb-6 lg:mb-8">
                        {plan.features.map((feature, index) => (
                          <li key={index} className="flex items-center gap-2 md:gap-3">
                            <div className="w-4 h-4 md:w-5 md:h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <Check className="h-2 w-2 md:h-3 md:w-3 text-green-600" />
                            </div>
                            <span className="text-xs md:text-sm lg:text-base text-gray-700">
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>

                      <div className="w-full bg-gray-100 text-gray-500 py-2 md:py-3 px-4 md:px-6 rounded-lg md:rounded-xl text-center text-sm md:text-base font-semibold">
                        {t('subscription.loginToSubscribe', 'Login to subscribe')}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6 md:py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Cancel Confirmation Modal */}
        {showCancelConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl md:rounded-2xl shadow-2xl max-w-md w-full">
              <div className="p-4 md:p-6">
                <div className="flex items-center gap-3 mb-3 md:mb-4">
                  <div className="p-2 bg-red-100 rounded-full">
                    <XCircle className="h-5 w-5 md:h-6 md:w-6 text-red-600" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-900">
                    {t('subscription.confirmCancelTitle', 'Cancel Subscription')}
                  </h3>
                </div>

                <div className="mb-4 md:mb-6">
                  <p className="text-sm md:text-base text-gray-600 mb-2 md:mb-3">
                    {t('subscription.confirmCancelMessage', 'Are you sure you want to cancel your subscription?')}
                  </p>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg md:rounded-xl p-3 md:p-4">
                    <div className="flex items-start gap-2 md:gap-3">
                      <Info className="h-4 w-4 md:h-5 md:w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-yellow-800 font-medium text-sm md:text-base mb-1 md:mb-2">
                          {t('subscription.cancellationNoteTitle', 'Important Information:')}
                        </p>
                        <ul className="text-yellow-700 text-xs md:text-sm space-y-1">
                          <li className="flex items-start gap-1 md:gap-2">
                            <span className="font-bold">•</span>
                            <span>{t('subscription.cancellationNote1', 'Your subscription will be downgraded to the Free plan')}</span>
                          </li>
                          <li className="flex items-start gap-1 md:gap-2">
                            <span className="font-bold">•</span>
                            <span>{t('subscription.cancellationNote2', 'You will lose access to Pro features immediately')}</span>
                          </li>
                          <li className="flex items-start gap-1 md:gap-2">
                            <span className="font-bold">•</span>
                            <span>{t('subscription.cancellationNote3', 'No refunds will be issued for the current billing period')}</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 md:gap-3 justify-end">
                  <button
                    onClick={hideCancelConfirmation}
                    disabled={cancelling}
                    className="px-3 md:px-4 py-1.5 md:py-2 text-gray-700 text-sm md:text-base font-medium rounded-lg md:rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-50"
                  >
                    {t('subscription.cancelNo', 'Keep Subscription')}
                  </button>
                  <button
                    onClick={handleCancelSubscription}
                    disabled={cancelling}
                    className="px-3 md:px-4 py-1.5 md:py-2 bg-red-600 text-white text-sm md:text-base font-medium rounded-lg md:rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 md:gap-2"
                  >
                    {cancelling ? (
                      <>
                        <Loader2 className="h-3 w-3 md:h-4 md:w-4 animate-spin" />
                        {t('subscription.cancelling', 'Cancelling...')}
                      </>
                    ) : (
                      t('subscription.cancelYes', 'Yes, Cancel')
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
            {t('subscription.title', 'Subscription Management')}
          </h1>
          <p className="text-base md:text-lg lg:text-xl text-gray-600">
            {t('subscription.subtitle', 'Manage your subscription and billing preferences')}
          </p>
        </div>

        {/* Current Subscription Status */}
        <div className={`rounded-xl md:rounded-2xl shadow-lg p-4 md:p-6 lg:p-8 mb-6 md:mb-8 ${currentPlanStyle.bg} border-2 ${currentPlanStyle.border}`}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4 md:mb-6">
            <div className="flex items-center gap-3 md:gap-4">
              <div className={`p-2 md:p-3 rounded-lg md:rounded-xl bg-gradient-to-r ${planGradients[currentPlan]}`}>
                {React.createElement(getPlanIcon(currentPlan), { className: "h-5 w-5 md:h-6 md:w-6 text-white" })}
              </div>
              <div>
                <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 capitalize">
                  {currentPlan === 'pro_monthly'
                    ? t('plans.pro.name', 'Professional')
                    : currentPlan === 'pro_yearly'
                      ? t('plans.pro.name', 'Professional')
                      : t(`plans.${currentPlan}.name`, currentPlan)} {t('subscription.plan', 'Plan')}
                </h2>
                <div className="flex flex-wrap items-center gap-1 md:gap-2 mt-1">
                  {isTrialActive && (
                    <span className="px-1.5 py-0.5 md:px-2 md:py-1 bg-green-100 text-green-800 text-xs rounded-full">
                      {t('subscription.trialActive', 'Trial Active')}
                    </span>
                  )}
                  {currentSubscription?.subscription?.cancel_at_period_end && (
                    <span className="px-1.5 py-0.5 md:px-2 md:py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                      {t('subscription.cancelling', 'Cancelling at period end')}
                    </span>
                  )}
                  {currentSubscription?.subscription?.status === 'canceled' && (
                    <span className="px-1.5 py-0.5 md:px-2 md:py-1 bg-red-100 text-red-800 text-xs rounded-full">
                      {t('subscription.cancelled', 'Cancelled')}
                    </span>
                  )}
                  <p className={`${currentPlanStyle.text} text-sm md:text-base`}>
                    {isActive
                      ? t('subscription.activeSubscription', 'Active subscription')
                      : t('subscription.noActiveSubscription', 'No active subscription')
                    }
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 md:gap-3 w-full sm:w-auto">
              <button
                onClick={handleSyncSubscription}
                disabled={syncing || !currentSubscription?.subscription}
                className="flex items-center gap-1.5 md:gap-2 bg-gray-600 text-white px-3 md:px-4 py-1.5 md:py-2 rounded-lg md:rounded-xl text-sm md:text-base hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto justify-center"
              >
                <RefreshCw className={`h-3.5 w-3.5 md:h-4 md:w-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? t('subscription.syncing', 'Syncing...') : t('subscription.sync', 'Sync')}
              </button>

              {/* Cancel Button - Only show if subscription is cancellable */}
              {isCancellable && (
                <button
                  onClick={showCancelConfirmation}
                  disabled={cancelling || isProcessing}
                  className="flex items-center gap-1.5 md:gap-2 bg-red-600 text-white px-3 md:px-4 py-1.5 md:py-2 rounded-lg md:rounded-xl text-sm md:text-base hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto justify-center"
                >
                  <XCircle className="h-3.5 w-3.5 md:h-4 md:w-4" />
                  <span className="hidden sm:inline">
                    {t('subscription.cancel', 'Cancel Subscription')}
                  </span>
                  <span className="sm:hidden">
                    {t('subscription.cancelShort', 'Cancel')}
                  </span>
                </button>
              )}
            </div>
          </div>

          {currentSubscription?.subscription ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 lg:gap-6">
              {/* Status Card */}
              <div className="bg-white/80 rounded-lg md:rounded-xl p-3 md:p-4 backdrop-blur-sm">
                <div className="flex items-center gap-2 mb-1.5 md:mb-2">
                  <div className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full ${(currentSubscription.subscription.status === 'active' &&
                    !currentSubscription.subscription.cancel_at_period_end) ||
                    currentSubscription.subscription.status === 'trialing'
                    ? 'bg-green-500'
                    : currentSubscription.subscription.status === 'past_due'
                      ? 'bg-yellow-500'
                      : currentSubscription.subscription.cancel_at_period_end
                        ? 'bg-orange-500'
                        : 'bg-red-500'
                    }`}></div>
                  <h3 className="font-semibold text-gray-900 text-xs md:text-sm lg:text-base">
                    {t('subscription.status', 'Status')}
                  </h3>
                </div>
                <p className={`text-xs md:text-sm lg:text-base capitalize font-medium ${(currentSubscription.subscription.status === 'active' &&
                  !currentSubscription.subscription.cancel_at_period_end) ||
                  currentSubscription.subscription.status === 'trialing'
                  ? 'text-green-600'
                  : currentSubscription.subscription.status === 'past_due'
                    ? 'text-yellow-600'
                    : currentSubscription.subscription.cancel_at_period_end
                      ? 'text-orange-600'
                      : 'text-red-600'
                  }`}>
                  {currentSubscription.subscription.cancel_at_period_end
                    ? t('subscription.cancelling_at_period_end', 'Cancelling at period end')
                    : t(`subscription.statuses.${currentSubscription.subscription.status}`,
                      currentSubscription.subscription.status)}
                </p>
                {currentSubscription.subscription.cancel_at_period_end && (
                  <p className="text-xs md:text-sm text-orange-600 mt-0.5 md:mt-1">
                    {t('subscription.downgraded_to_free', 'Downgraded to Free plan')}
                  </p>
                )}
              </div>

              {/* Billing Date Card */}
              <div className="bg-white/80 rounded-lg md:rounded-xl p-3 md:p-4 backdrop-blur-sm">
                <div className="flex items-center gap-1.5 md:gap-2 mb-1.5 md:mb-2">
                  <Calendar className="h-3.5 w-3.5 md:h-4 md:w-4 text-gray-500" />
                  <h3 className="font-semibold text-gray-900 text-xs md:text-sm lg:text-base">
                    {currentSubscription.subscription.cancel_at_period_end ||
                      currentSubscription.subscription.status === 'canceled'
                      ? t('subscription.accessUntil', 'Access Until')
                      : t('subscription.nextBilling', 'Next Billing')
                    }
                  </h3>
                </div>
                <p className="text-gray-600 text-xs md:text-sm lg:text-base">
                  {currentSubscription.subscription.current_period_end
                    ? new Date(currentSubscription.subscription.current_period_end).toLocaleDateString(currentLanguage)
                    : t('subscription.notAvailable', 'N/A')
                  }
                </p>
                {(currentSubscription.subscription.cancel_at_period_end ||
                  currentSubscription.subscription.status === 'canceled') && (
                    <p className="text-xs md:text-sm text-yellow-600 mt-0.5 md:mt-1">
                      {t('subscription.willDowngrade', 'Will downgrade to Free plan')}
                    </p>
                  )}
              </div>

              {/* Features Card */}
              <div className="bg-white/80 rounded-lg md:rounded-xl p-3 md:p-4 backdrop-blur-sm">
                <div className="flex items-center gap-1.5 md:gap-2 mb-1.5 md:mb-2">
                  <Check className="h-3.5 w-3.5 md:h-4 md:w-4 text-gray-500" />
                  <h3 className="font-semibold text-gray-900 text-xs md:text-sm lg:text-base">
                    {t('subscription.planFeatures', 'Plan Features')}
                  </h3>
                </div>
                <p className="text-gray-600 text-xs md:text-sm lg:text-base">
                  {currentSubscription.plan_limits?.features?.length ||
                    currentSubscription.subscription?.features?.length ||
                    0} {t('subscription.featuresIncluded', 'features included')}
                </p>
              </div>

              {/* Trial or Billing Cycle Card */}
              {isTrialActive && currentSubscription.trial_end_date ? (
                <div className="bg-green-50 rounded-lg md:rounded-xl p-3 md:p-4">
                  <div className="flex items-center gap-1.5 md:gap-2 mb-1.5 md:mb-2">
                    <Zap className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-600" />
                    <h3 className="font-semibold text-gray-900 text-xs md:text-sm lg:text-base">
                      {t('subscription.trialEnds', 'Trial Ends')}
                    </h3>
                  </div>
                  <p className="text-green-700 font-medium text-xs md:text-sm lg:text-base">
                    {new Date(currentSubscription.trial_end_date).toLocaleDateString(currentLanguage)}
                  </p>
                  <p className="text-xs md:text-sm text-green-600 mt-0.5 md:mt-1">
                    {t('subscription.daysRemaining', `${currentSubscription.trial_days_remaining || 0} days remaining`, { count: currentSubscription.trial_days_remaining || 0 })}
                  </p>
                </div>
              ) : (
                <div className="bg-white/80 rounded-lg md:rounded-xl p-3 md:p-4">
                  <div className="flex items-center gap-1.5 md:gap-2 mb-1.5 md:mb-2">
                    <CreditCard className="h-3.5 w-3.5 md:h-4 md:w-4 text-gray-500" />
                    <h3 className="font-semibold text-gray-900 text-xs md:text-sm lg:text-base">
                      {t('subscription.billingCycle', 'Billing Cycle')}
                    </h3>
                  </div>
                  <p className="text-gray-600 text-xs md:text-sm lg:text-base capitalize">
                    {currentSubscription.subscription?.interval === 'year'
                      ? t('subscription.intervals.year', 'Year')
                      : t('subscription.intervals.month', 'Month')}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4 md:py-6">
              <p className="text-sm md:text-base text-gray-600">
                {t('subscription.noSubscriptionDetails', 'No active subscription details available')}
              </p>
            </div>
          )}
        </div>

        {/* Available Plans */}
        <div className="mb-6 md:mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">
            {t('subscription.availablePlans', 'Available Plans')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
            {plans.map((plan) => {
              const Icon = planIcons[plan.id] || Zap;
              const isCurrentPlan = plan.id === currentPlan;
              const isUpgrade = canUpgrade() && plan.price > 0 && !isCurrentPlan;
              const planStyle = planColors[plan.id] || planColors.free;

              return (
                <div
                  key={plan.id}
                  className={`relative bg-white rounded-xl md:rounded-2xl shadow-lg border-2 transition-all duration-300 ${isCurrentPlan
                    ? `${planStyle.border} ring-2 md:ring-4 ${planStyle.ring} transform scale-[1.02]`
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-xl'
                    }`}
                >
                  {isCurrentPlan && (
                    <div className="absolute -top-2 md:-top-3 left-1/2 transform -translate-x-1/2">
                      <div className={`${planStyle.badge} text-white px-3 md:px-6 py-1 md:py-2 rounded-full text-xs md:text-sm font-semibold shadow-lg whitespace-nowrap`}>
                        {t('subscription.currentPlan', 'Current Plan')}
                      </div>
                    </div>
                  )}

                  <div className="p-4 md:p-6 lg:p-8">
                    {/* Plan Header */}
                    <div className="text-center mb-4 md:mb-6">
                      <div className={`inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl bg-gradient-to-r ${planGradients[plan.id] || 'from-gray-500 to-gray-600'
                        } text-white mb-3 md:mb-4`}>
                        <Icon className="h-5 w-5 md:h-8 md:w-8" />
                      </div>
                      <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-1 md:mb-2">
                        {plan.id === 'pro_monthly'
                          ? t('plans.pro.name', 'Professional')
                          : plan.id === 'pro_yearly'
                            ? t('plans.pro.name', 'Professional')
                            : t(`plans.${plan.id}.name`, plan.name)}
                      </h3>
                      <div className="flex items-baseline justify-center gap-1 md:gap-2">
                        <span className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">
                          CHF {plan.price}
                        </span>
                        <span className="text-sm md:text-base text-gray-600">
                          /{t(`subscription.intervals.${plan.interval}`, plan.interval)}
                        </span>
                      </div>
                    </div>

                    {/* Action Button */}
                    <div className="mt-auto">
                      {isCurrentPlan ? (
                        <div className={`w-full ${planStyle.bg} ${planStyle.text} py-2 md:py-3 px-4 md:px-6 rounded-lg md:rounded-xl text-center text-sm md:text-base font-semibold border ${planStyle.border}`}>
                          {t('subscription.currentPlan', 'Current Plan')}
                        </div>
                      ) : isUpgrade ? (
                        <button
                          onClick={() => handleUpgrade(plan)}
                          disabled={isProcessing || !plan.stripe_price_id}
                          className="w-full bg-blue-600 text-white py-2 md:py-3 px-4 md:px-6 rounded-lg md:rounded-xl text-sm md:text-base font-semibold hover:bg-blue-700 hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isProcessing
                            ? t('subscription.processing', 'Processing...')
                            : t('subscription.upgradeNow', 'Upgrade Now')
                          }
                        </button>
                      ) : plan.id === 'free' ? (
                        <div className="w-full bg-gray-100 text-gray-500 py-2 md:py-3 px-4 md:px-6 rounded-lg md:rounded-xl text-center text-sm md:text-base font-semibold">
                          {t('subscription.freePlan', 'Free Plan')}
                        </div>
                      ) : (
                        <div className="w-full bg-gray-100 text-gray-500 py-2 md:py-3 px-4 md:px-6 rounded-lg md:rounded-xl text-center text-sm md:text-base font-semibold">
                          {t('subscription.notAvailable', 'Not Available')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Important Notes Section */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-6 border border-gray-200">
          <h3 className="text-base md:text-lg font-bold text-gray-900 mb-3 md:mb-4">
            {t('subscription.importantNotes', 'Important Notes')}
          </h3>
          <ul className="space-y-2 md:space-y-3 text-gray-600">
            <li className="flex items-start gap-2 md:gap-3">
              <div className="w-4 h-4 md:w-5 md:h-5 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Info className="h-2 w-2 md:h-3 md:w-3 text-blue-600" />
              </div>
              <span className="text-xs md:text-sm lg:text-base">
                {t('subscription.note2', 'No refunds are issued for partial billing periods.')}
              </span>
            </li>
            <li className="flex items-start gap-2 md:gap-3">
              <div className="w-4 h-4 md:w-5 md:h-5 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Info className="h-2 w-2 md:h-3 md:w-3 text-blue-600" />
              </div>
              <span className="text-xs md:text-sm lg:text-base">
                {t('subscription.note3', 'You can re-subscribe at any time.')}
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Subscription;